import React from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { TrendingUp, TrendingDown, Equal, Plus, RefreshCw, Trash2 } from 'lucide-react';

interface PatternComparisonResult {
  pattern: string;
  source1_data?: any;
  source2_data?: any;
  status: 'identical' | 'different_values' | 'only_in_source1' | 'only_in_source2';
  differences?: {
    frequency_diff?: number;
    success_rate_diff?: number;
    total_sequences_diff?: number;
  };
}

interface CrossPatternDetailModalProps {
  pattern: PatternComparisonResult | null;
  isOpen: boolean;
  onClose: () => void;
  source1Name: string;
  source2Name: string;
}

export const CrossPatternDetailModal: React.FC<CrossPatternDetailModalProps> = ({
  pattern,
  isOpen,
  onClose,
  source1Name,
  source2Name
}) => {

  const getStatusInfo = (status: string) => {
    const statusMap = {
      identical: {
        color: 'bg-green-500/10 text-green-600 border-green-500/20',
        icon: <Equal className="h-4 w-4" />,
        description: 'Pattern exists in both sources with identical values'
      },
      different_values: {
        color: 'bg-blue-500/10 text-blue-600 border-blue-500/20',
        icon: <RefreshCw className="h-4 w-4" />,
        description: 'Pattern exists in both sources but with different values'
      },
      only_in_source1: {
        color: 'bg-orange-500/10 text-orange-600 border-orange-500/20',
        icon: <Plus className="h-4 w-4" />,
        description: `Pattern only exists in ${source1Name}`
      },
      only_in_source2: {
        color: 'bg-purple-500/10 text-purple-600 border-purple-500/20',
        icon: <Plus className="h-4 w-4" />,
        description: `Pattern only exists in ${source2Name}`
      }
    };

    return statusMap[status as keyof typeof statusMap] || statusMap.identical;
  };

  const formatChange = (value: number | null | undefined, isPercentage = false): string => {
    if (value === null || value === undefined) return 'N/A';
    const sign = value > 0 ? '+' : '';
    const suffix = isPercentage ? '%' : '';
    return `${sign}${value.toFixed(2)}${suffix}`;
  };

  const getChangeColor = (value: number | null | undefined): string => {
    if (!value || value === 0) return 'text-gray-600';
    return value > 0 ? 'text-green-600' : 'text-red-600';
  };

  const getChangeIcon = (value: number | null | undefined) => {
    if (!value || value === 0) return <Equal className="h-4 w-4 text-gray-400" />;
    if (value > 0) return <TrendingUp className="h-4 w-4 text-green-500" />;
    return <TrendingDown className="h-4 w-4 text-red-500" />;
  };

  if (!pattern) return null;

  const statusInfo = getStatusInfo(pattern.status);

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            Pattern Comparison Details
            <Badge className={statusInfo.color}>
              {statusInfo.icon}
              <span className="ml-1 capitalize">{pattern.status.replace(/_/g, ' ')}</span>
            </Badge>
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-6">
          {/* Pattern Info */}
          <Card>
            <CardHeader>
              <CardTitle>Pattern Information</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div>
                  <span className="text-sm text-muted-foreground">Pattern Sequence:</span>
                  <div className="text-lg font-mono bg-muted p-2 rounded">
                    {pattern.pattern || 'N/A'}
                  </div>
                </div>
                <div>
                  <span className="text-sm text-muted-foreground">Status:</span>
                  <p className="text-sm mt-1">{statusInfo.description}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Data Comparison */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Source 1 Data */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">{source1Name}</CardTitle>
              </CardHeader>
              <CardContent>
                {pattern.source1_data ? (
                  <div className="space-y-3">
                    <div>
                      <span className="text-sm text-muted-foreground">Frequency:</span>
                      <div className="text-xl font-semibold">{pattern.source1_data.frequency}</div>
                    </div>
                    <div>
                      <span className="text-sm text-muted-foreground">Success Rate:</span>
                      <div className="text-xl font-semibold">
                        {pattern.source1_data.success_rate?.toFixed(2)}%
                      </div>
                    </div>
                    <div>
                      <span className="text-sm text-muted-foreground">Win Count:</span>
                      <div className="text-lg">{pattern.source1_data.next_win_count}</div>
                    </div>
                    <div>
                      <span className="text-sm text-muted-foreground">Loss Count:</span>
                      <div className="text-lg">{pattern.source1_data.next_loss_count}</div>
                    </div>
                    <div>
                      <span className="text-sm text-muted-foreground">Total Sequences:</span>
                      <div className="text-lg">{pattern.source1_data.total_sequences}</div>
                    </div>
                  </div>
                ) : (
                  <div className="text-center py-8 text-muted-foreground">
                    Pattern not found in this source
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Source 2 Data */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">{source2Name}</CardTitle>
              </CardHeader>
              <CardContent>
                {pattern.source2_data ? (
                  <div className="space-y-3">
                    <div>
                      <span className="text-sm text-muted-foreground">Frequency:</span>
                      <div className="text-xl font-semibold">{pattern.source2_data.frequency}</div>
                    </div>
                    <div>
                      <span className="text-sm text-muted-foreground">Success Rate:</span>
                      <div className="text-xl font-semibold">
                        {pattern.source2_data.success_rate?.toFixed(2)}%
                      </div>
                    </div>
                    <div>
                      <span className="text-sm text-muted-foreground">Win Count:</span>
                      <div className="text-lg">{pattern.source2_data.next_win_count}</div>
                    </div>
                    <div>
                      <span className="text-sm text-muted-foreground">Loss Count:</span>
                      <div className="text-lg">{pattern.source2_data.next_loss_count}</div>
                    </div>
                    <div>
                      <span className="text-sm text-muted-foreground">Total Sequences:</span>
                      <div className="text-lg">{pattern.source2_data.total_sequences}</div>
                    </div>
                  </div>
                ) : (
                  <div className="text-center py-8 text-muted-foreground">
                    Pattern not found in this source
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Differences Analysis */}
          {pattern.differences && pattern.status === 'different_values' && (
            <Card>
              <CardHeader>
                <CardTitle>Differences Analysis</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  <div className="text-center">
                    <span className="text-sm text-muted-foreground block mb-1">Frequency Change</span>
                    <div className="flex items-center justify-center gap-2">
                      {getChangeIcon(pattern.differences.frequency_diff)}
                      <span className={`text-xl font-semibold ${getChangeColor(pattern.differences.frequency_diff)}`}>
                        {formatChange(pattern.differences.frequency_diff)}
                      </span>
                    </div>
                  </div>
                  <div className="text-center">
                    <span className="text-sm text-muted-foreground block mb-1">Success Rate Change</span>
                    <div className="flex items-center justify-center gap-2">
                      {getChangeIcon(pattern.differences.success_rate_diff)}
                      <span className={`text-xl font-semibold ${getChangeColor(pattern.differences.success_rate_diff)}`}>
                        {formatChange(pattern.differences.success_rate_diff, true)}
                      </span>
                    </div>
                  </div>
                  <div className="text-center">
                    <span className="text-sm text-muted-foreground block mb-1">Total Sequences Change</span>
                    <div className="flex items-center justify-center gap-2">
                      {getChangeIcon(pattern.differences.total_sequences_diff)}
                      <span className={`text-xl font-semibold ${getChangeColor(pattern.differences.total_sequences_diff)}`}>
                        {formatChange(pattern.differences.total_sequences_diff)}
                      </span>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Impact Analysis */}
          {pattern.status === 'different_values' && pattern.differences && (
            <Card>
              <CardHeader>
                <CardTitle>Impact Analysis</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {pattern.differences.frequency_diff && Math.abs(pattern.differences.frequency_diff) > 0 && (
                    <p className="text-sm">
                      • Frequency {pattern.differences.frequency_diff > 0 ? 'increased' : 'decreased'} by{' '}
                      <span className="font-medium">{Math.abs(pattern.differences.frequency_diff)}</span> occurrences
                    </p>
                  )}
                  {pattern.differences.success_rate_diff && Math.abs(pattern.differences.success_rate_diff) > 0.01 && (
                    <p className="text-sm">
                      • Success rate {pattern.differences.success_rate_diff > 0 ? 'improved' : 'declined'} by{' '}
                      <span className="font-medium">{Math.abs(pattern.differences.success_rate_diff).toFixed(2)}%</span>
                    </p>
                  )}
                  {pattern.differences.total_sequences_diff && Math.abs(pattern.differences.total_sequences_diff) > 0 && (
                    <p className="text-sm">
                      • Total data points {pattern.differences.total_sequences_diff > 0 ? 'increased' : 'decreased'} by{' '}
                      <span className="font-medium">{Math.abs(pattern.differences.total_sequences_diff)}</span>
                    </p>
                  )}
                  {pattern.status === 'different_values' && 
                   (!pattern.differences.frequency_diff || Math.abs(pattern.differences.frequency_diff) === 0) &&
                   (!pattern.differences.success_rate_diff || Math.abs(pattern.differences.success_rate_diff) <= 0.01) &&
                   (!pattern.differences.total_sequences_diff || Math.abs(pattern.differences.total_sequences_diff) === 0) && (
                    <p className="text-sm text-muted-foreground">
                      Minor changes detected in pattern metrics
                    </p>
                  )}
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};